const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://nsgruve.ai.pocsolution.com';

class GenericChatService {
  constructor(token) {
    this.token = token;
    this.wsConnection = null;
    this.connectionStatus = 'disconnected';
    this.messageHandlers = new Set();
    this.statusHandlers = new Set();
    this.processedMessageIds = new Set(); // Track processed message IDs to prevent duplicates
    this.currentSessionId = null; // Track current session to prevent duplicate connections

    // NEW: V2 workflow state tracking
    this.v2RunId = null;
    this.v2MessageCount = 0; // Track displayed messages for progressive display
    this.isV2Connection = false;
    this.isV2Workflow = false;
    this.workflowId = null;
  }

  // Subscribe to message events
  onMessage(handler) {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  // Subscribe to connection status events
  onStatusChange(handler) {
    this.statusHandlers.add(handler);
    return () => this.statusHandlers.delete(handler);
  }

  // Emit message to all handlers
  emitMessage(data) {
    this.messageHandlers.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        console.error('Error in message handler:', error);
      }
    });
  }

  // Emit status change to all handlers
  emitStatusChange(status) {
    this.connectionStatus = status;
    this.statusHandlers.forEach(handler => {
      try {
        handler(status);
      } catch (error) {
        console.error('Error in status handler:', error);
      }
    });
  }

  // Create a simple chat session
  async createSession(sessionName = null) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.token}`
        },
        body: JSON.stringify({
          agent_id: "1", // Default agent
          session_name: sessionName || `Chat ${new Date().toLocaleTimeString()}`
        })
      });

      if (response.ok) {
        const data = await response.json();
        return data.session_id;
      } else {
        throw new Error(`Failed to create session: ${response.status}`);
      }
    } catch (error) {
      console.error('Error creating session:', error);
      throw error;
    }
  }

  // Get session details (metadata like workflow_id, etc.)
  async getSessionDetails(sessionId) {
    try {
      // Use specific session endpoint for better performance (instead of fetching all sessions)
      const response = await fetch(`${API_BASE_URL}/api/chat/sessions/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Return session data directly (endpoint returns {session: {...}, messages: [...]})
        return data.session || null;
      } else {
        throw new Error(`Failed to get session details: ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to get session details:', error);
      return null;
    }
  }

  // Load existing session messages
  async loadSession(sessionId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/sessions/${sessionId}/messages`, {
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Transform messages to consistent format
        const messages = (data.messages || []).map(msg => ({
          id: msg.id || msg.message_id,
          type: msg.type || msg.message_type || 'assistant',
          content: msg.content || msg.message_content,
          timestamp: msg.timestamp || msg.created_at,
          metadata: msg.metadata || msg.message_metadata || {},
          step_name: msg.step_name  // Required for modal opening logic
        }));

        // Sort messages by timestamp to ensure chronological order
        return messages.sort((a, b) => {
          const timeA = new Date(a.timestamp).getTime();
          const timeB = new Date(b.timestamp).getTime();
          return timeA - timeB;
        });
      } else {
        throw new Error(`Failed to load session: ${response.status}`);
      }
    } catch (error) {
      console.error('Error loading session:', error);
      throw error;
    }
  }

  // Connect WebSocket for real-time updates
  connectWebSocket(sessionId) {
    // Prevent duplicate connections to the same session
    if (this.wsConnection && this.wsConnection.readyState === WebSocket.OPEN && this.currentSessionId === sessionId) {
      console.log('⚠️ FRONTEND DEBUG: WebSocket already connected to session', sessionId);
      return this.wsConnection;
    }
    
    if (this.wsConnection) {
      console.log('🔌 FRONTEND DEBUG: Closing existing WebSocket connection');
      this.wsConnection.close();
    }
    
    // Clear processed message IDs for new session
    this.processedMessageIds.clear();
    this.currentSessionId = sessionId;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/api/chat/ws/${sessionId}?token=${this.token}`;
    
    console.log('🔌 FRONTEND DEBUG: Creating new WebSocket connection to', wsUrl);
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      console.log('✅ FRONTEND DEBUG: WebSocket connected to session', sessionId);
      this.emitStatusChange('connected');
      this.wsConnection = ws;
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handleWebSocketMessage(data);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };
    
    ws.onclose = () => {
      this.emitStatusChange('disconnected');
      this.wsConnection = null;
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      this.emitStatusChange('error');
    };

    return ws;
  }

  // Handle WebSocket messages (enhanced for workflow support)
  handleWebSocketMessage(data) {
    console.log('🔄 FRONTEND DEBUG: GenericChatService handling message', { type: data.type, message_type: data.message_type });
    
    if (data.type === 'message' || data.type === 'assistant_message') {
      console.log('💬 FRONTEND DEBUG: Processing message/assistant_message');
      // Only emit assistant messages through WebSocket
      if (data.message_type === 'assistant' || data.type === 'assistant_message') {
        const messageId = data.message_id || `msg_${Date.now()}_${Math.random()}`;
        
        // Check for duplicates
        if (this.processedMessageIds.has(messageId)) {
          console.log('⚠️ FRONTEND DEBUG: Skipping duplicate message', messageId);
          return;
        }
        
        const message = {
          id: messageId,
          type: 'assistant',
          content: data.content,
          timestamp: data.timestamp || new Date().toISOString(),
          metadata: data.metadata
        };
        
        console.log('🤖 FRONTEND DEBUG: Adding assistant message', message);
        this.processedMessageIds.add(messageId);
        this.emitMessage({ type: 'new_message', message });
      }
    } else if (data.type === 'workflow_update') {
      console.log('🔧 FRONTEND DEBUG: Processing workflow_update');
      const updateData = data.data || data;
      console.log('🔧 FRONTEND DEBUG: Update data:', updateData);
      
      // Check the type field in the nested data for assistant messages
      if (updateData.type === 'assistant') {
        const messageId = updateData.message_id || `workflow_msg_${Date.now()}_${Math.random()}`;
        
        // Check for duplicates
        if (this.processedMessageIds.has(messageId)) {
          console.log('⚠️ FRONTEND DEBUG: Skipping duplicate workflow message', messageId);
          return;
        }
        
        // AI responses during workflow execution
        const assistantMessage = {
          id: messageId,
          type: 'assistant',
          content: updateData.content,
          timestamp: updateData.timestamp || new Date().toISOString(),
          metadata: updateData.metadata
        };
        console.log('🤖 FRONTEND DEBUG: Adding assistant message from workflow_update', assistantMessage);
        this.processedMessageIds.add(messageId);
        this.emitMessage({ type: 'new_message', message: assistantMessage });
        
      } else if (updateData.message_type === 'workflow_step') {
        // Step execution updates
        const stepMessage = {
          id: Date.now(),
          type: 'workflow',
          content: `🔄 ${updateData.content}`,
          timestamp: updateData.timestamp || new Date().toISOString(),
          metadata: updateData.metadata
        };
        this.emitMessage({ type: 'new_message', message: stepMessage });
        
      } else if (updateData.message_type === 'assistant') {
        const messageId = updateData.message_id || `legacy_msg_${Date.now()}_${Math.random()}`;
        
        // Check for duplicates
        if (this.processedMessageIds.has(messageId)) {
          console.log('⚠️ FRONTEND DEBUG: Skipping duplicate legacy message', messageId);
          return;
        }
        
        // AI responses during workflow execution (legacy check)
        const assistantMessage = {
          id: messageId,
          type: 'assistant',
          content: updateData.content,
          timestamp: updateData.timestamp || new Date().toISOString(),
          metadata: updateData.metadata
        };
        console.log('🤖 FRONTEND DEBUG: Adding assistant message from workflow_update (legacy)', assistantMessage);
        this.processedMessageIds.add(messageId);
        this.emitMessage({ type: 'new_message', message: assistantMessage });
        
      } else if (updateData.message_type === 'tool_call') {
        // Tool call being made
        const toolCallMessage = {
          id: updateData.tool_call_id || Date.now(),
          type: 'tool_call',
          content: updateData.content,
          timestamp: updateData.timestamp || new Date().toISOString(),
          metadata: updateData.metadata,
          toolName: updateData.tool_name,
          toolArguments: updateData.tool_arguments,
          status: 'calling'
        };
        this.emitMessage({ type: 'new_message', message: toolCallMessage });
        
      } else if (updateData.message_type === 'tool_result') {
        // Tool execution results
        const toolResultMessage = {
          id: Date.now(),
          type: 'tool_result',
          content: updateData.content,
          timestamp: updateData.timestamp || new Date().toISOString(),
          metadata: updateData.metadata,
          toolName: updateData.tool_name,
          toolResult: updateData.tool_result,
          success: updateData.success,
          errorMessage: updateData.error_message,
          executionTime: updateData.execution_time,
          toolCallId: updateData.tool_call_id
        };
        this.emitMessage({ type: 'new_message', message: toolResultMessage });
      }
      
    } else if (data.type === 'workflow_step') {
      // Handle legacy workflow step updates (backwards compatibility)
      const stepMessage = {
        id: Date.now(),
        type: 'workflow',
        content: `🔄 ${data.step_name}: ${data.content}`,
        timestamp: data.timestamp,
        metadata: data.metadata
      };
      this.emitMessage({ type: 'new_message', message: stepMessage });
      
    } else if (data.type === 'system') {
      // Handle system messages (workflow completion, errors, etc.)
      console.log('🏁 FRONTEND DEBUG: Processing system message', data);
      const systemMessage = {
        id: Date.now(),
        type: 'system',
        content: data.content,
        timestamp: data.timestamp || new Date().toISOString(),
        metadata: data.metadata
      };
      this.emitMessage({ type: 'new_message', message: systemMessage });
      
    } else if (data.type === 'error') {
      console.error('Workflow chat error:', data.error);
      this.emitMessage({ type: 'error', error: data.error });
    }
  }

  // Send a message using default settings
  async sendMessage(sessionId, message, provider = 'openai', model = 'gpt-4o') {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.token}`
        },
        body: JSON.stringify({
          session_id: sessionId,
          message: message,
          provider: provider,
          model: model
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to send message: ${response.status}`);
      }

      return true;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  // Check if session has workflow execution logs
  async hasExecutionLogs(sessionId) {
    try {
      // This is a simple check - in reality you might want to call a specific endpoint
      // For now, we'll assume any session could potentially have logs
      return true;
    } catch (error) {
      console.error('Error checking execution logs:', error);
      return false;
    }
  }

  // NEW: V2 workflow management methods
  setV2Workflow(isV2, workflowId = null) {
    this.isV2Workflow = isV2;
    this.workflowId = workflowId;
    console.log('🎯 FRONTEND DEBUG: Set V2 workflow mode', { isV2, workflowId });
  }

  getV2RunId() {
    return this.v2RunId;
  }

  isV2WorkflowActive() {
    return this.isV2Workflow && this.v2RunId;
  }

  // NEW: Execute V2 workflow
  async executeWorkflowV2(workflowId, userMessage, existingSessionId = null) {
    try {
      console.log('🚀 FRONTEND DEBUG: Starting V2 workflow execution', { workflowId, userMessage });

      const response = await fetch(`${API_BASE_URL}/api/v2/workflows/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.token}`
        },
        body: JSON.stringify({
          workflow_id: parseInt(workflowId),
          user_message: userMessage,
          token: this.token,
          existing_session_id: existingSessionId // Pass existing session to continue in same chat
        })
      });

      const data = await response.json();

      if (data.success) {
        // Store V2 run_id
        this.v2RunId = data.run_id;

        console.log('✅ FRONTEND DEBUG: V2 workflow started successfully', { run_id: data.run_id });

        // Connect V2 WebSocket AFTER workflow is started and session is created
        await this.connectV2WebSocket(data.run_id);

        // Emit workflow started event
        this.emitMessage({
          type: 'workflow_started',
          run_id: data.run_id,
          workflow_id: workflowId
        });

        // Note: Removed unnecessary continueWorkflowV2 call that was causing duplicate message emission
        console.log('🔄 FRONTEND DEBUG: Workflow execution will proceed with backend processing');

        // Initialize message count with current session messages
        const sessionId = await this.getSessionIdFromRunId(data.run_id);
        if (sessionId) {
          const existingMessages = await this.loadSession(sessionId);
          const userMessagesCount = existingMessages.filter(m => m.type === 'user').length;
          // Start from the count BEFORE adding the current user message
          this.v2MessageCount = existingMessages.length - (userMessagesCount > 1 ? 0 : 1);
        }

        // Fetch and emit NEW messages only
        await this.fetchAndEmitV2Messages(data.run_id, this.v2MessageCount);

        // Update message count after fetching
        if (sessionId) {
          const messages = await this.loadSession(sessionId);
          this.v2MessageCount = messages.length;
        }

        return { success: true, run_id: data.run_id };
      } else {
        throw new Error(data.error || 'Failed to start V2 workflow');
      }
    } catch (error) {
      console.error('❌ FRONTEND DEBUG: Failed to execute V2 workflow:', error);
      throw error;
    }
  }

  // NEW: Continue V2 workflow execution
  async continueWorkflowV2(userMessage) {
    if (!this.v2RunId) {
      throw new Error('No active V2 workflow to continue');
    }

    try {
      console.log('🔄 FRONTEND DEBUG: Continuing V2 workflow', { run_id: this.v2RunId, userMessage });

      const response = await fetch(`${API_BASE_URL}/api/v2/workflows/continue/${this.v2RunId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.token}`
        },
        body: JSON.stringify({
          user_message: userMessage,
          token: this.token
        })
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to continue V2 workflow');
      }

      console.log('✅ FRONTEND DEBUG: V2 workflow continued successfully');

      // Poll for new messages with timeout
      console.log('📊 FRONTEND DEBUG: Current v2MessageCount before polling:', this.v2MessageCount);
      await this.pollForNewMessages(this.v2RunId, this.v2MessageCount);

      return { success: true };
    } catch (error) {
      console.error('❌ FRONTEND DEBUG: Failed to continue V2 workflow:', error);
      throw error;
    }
  }

  // NEW: Fetch and emit V2 workflow messages with progressive display (matches live chat behavior)
  async fetchAndEmitV2Messages(runId, currentMessageCount = 0) {
    try {
      console.log('📥 FRONTEND DEBUG: Fetching V2 workflow messages for run_id:', runId);

      // Get the session_id associated with this run_id
      const sessionId = await this.getSessionIdFromRunId(runId);
      if (!sessionId) {
        console.error('❌ FRONTEND DEBUG: Could not get session_id for run_id:', runId);
        return;
      }

      // Fetch messages from the session
      const messages = await this.loadSession(sessionId);

      // Only emit NEW messages (skip messages we've already shown)
      const newMessages = messages.slice(currentMessageCount);

      console.log('📊 FRONTEND DEBUG: Message count analysis:', {
        totalMessages: messages.length,
        currentMessageCount,
        newMessagesCount: newMessages.length,
        allMessageTypes: messages.map(m => m.type),
        newMessageTypes: newMessages.map(m => m.type)
      });

      if (newMessages.length === 0) {
        console.log('📭 FRONTEND DEBUG: No new messages to emit');
        return;
      }

      console.log('🔄 FRONTEND DEBUG: Emitting', newMessages.length, 'new messages progressively (matching live chat)');

      // Group messages by step_name for timing (exactly like live chat)
      const messagesByStep = {};
      newMessages.forEach(msg => {
        const key = msg.metadata?.step_name || 'general';
        if (!messagesByStep[key]) messagesByStep[key] = [];
        messagesByStep[key].push(msg);
      });

      // Add messages with progressive timing (exactly like live chat)
      Object.values(messagesByStep).forEach((stepMessages, stepIndex) => {
        stepMessages.forEach((msg, msgIndex) => {
          const delay = (stepIndex * 1000) + (msgIndex * 500); // 1s between steps, 500ms between messages in same step

          setTimeout(() => {
            // Check if this message was already processed to prevent duplicates
            if (!this.processedMessageIds.has(msg.id)) {
              console.log('📨 FRONTEND DEBUG: Emitting V2 message with', delay, 'ms delay:', msg);
              this.processedMessageIds.add(msg.id);
              this.emitMessage({
                type: 'new_message',
                message: msg
              });
            } else {
              console.log('⏭️  FRONTEND DEBUG: Skipping duplicate message:', msg.id);
            }
          }, delay);
        });
      });

      console.log('✅ FRONTEND DEBUG: Scheduled', newMessages.length, 'new V2 workflow messages for progressive display');
    } catch (error) {
      console.error('❌ FRONTEND DEBUG: Failed to fetch V2 messages:', error);
    }
  }

  // NEW: V2 WebSocket connection (FIXED: Use V1 WebSocket system for reliability)
  async connectV2WebSocket(runId) {
    // Close existing V1 connection if any
    if (this.wsConnection) {
      console.log('🔌 FRONTEND DEBUG: Closing existing WebSocket connection for V2');
      this.wsConnection.close();
    }

    // Add a small delay to ensure the workflow session is created in the database
    console.log('⏳ FRONTEND DEBUG: Waiting for session creation...');
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Get session_id for the run_id to connect to V1 WebSocket system
    const sessionId = await this.getSessionIdFromRunId(runId);
    if (!sessionId) {
      console.error('❌ FRONTEND DEBUG: Could not get session_id for run_id:', runId);
      return;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/api/chat/ws/${sessionId}?token=${this.token}`;

    console.log('🔌 FRONTEND DEBUG: Creating V2 WebSocket connection to V1 system:', wsUrl);
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('✅ FRONTEND DEBUG: V2 WebSocket connected to run', runId);
      this.emitStatusChange('connected');
      this.wsConnection = ws;
      this.isV2Connection = true;
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Use regular V1 message handling since we're connecting to V1 WebSocket
        // but flag it as V2 workflow context
        console.log('🔄 FRONTEND DEBUG: V2 workflow message via V1 WebSocket:', data);
        this.handleWebSocketMessage(data);
      } catch (error) {
        console.error('❌ FRONTEND DEBUG: Error parsing V2 WebSocket message:', error);
      }
    };

    ws.onclose = () => {
      console.log('🔌 FRONTEND DEBUG: V2 WebSocket disconnected');
      this.emitStatusChange('disconnected');
      this.wsConnection = null;
      this.isV2Connection = false;
    };

    ws.onerror = (error) => {
      console.error('❌ FRONTEND DEBUG: V2 WebSocket error:', error);
      this.emitStatusChange('error');
    };

    return ws;
  }

  // NEW: Handle V2 WebSocket messages
  handleV2WebSocketMessage(data) {
    // Handle wrapped event structure from WebSocket handler
    const eventData = data.event || data; // Extract nested event if wrapped
    const eventType = eventData.event_type;

    console.log('🔄 FRONTEND DEBUG: GenericChatService handling V2 message', {
      raw_event_type: data.event_type,
      extracted_event_type: eventType,
      message_type: data.type
    });

    switch (eventType) {
      case 'workflow.started':
        this.emitMessage({
          type: 'new_message',
          message: {
            id: Date.now(),
            type: 'system',
            content: `🚀 V2 Workflow started`,
            timestamp: new Date().toISOString(),
            v2_run_id: eventData.data?.run_id || eventData.run_id
          }
        });
        break;

      case 'step.started':
        this.emitMessage({
          type: 'new_message',
          message: {
            id: Date.now(),
            type: 'system',
            content: `⚡ Starting step: ${eventData.data?.step_name || 'Unknown'}`,
            timestamp: eventData.data?.timestamp || eventData.timestamp,
            v2_run_id: this.v2RunId,
            metadata: {
              step_name: eventData.data?.step_name,
              step_type: eventData.data?.step_type
            }
          }
        });
        break;

      case 'step.completed':
        this.emitMessage({
          type: 'new_message',
          message: {
            id: Date.now(),
            type: 'assistant',
            content: eventData.data?.ai_response || 'Step completed',
            timestamp: eventData.data?.timestamp || eventData.timestamp,
            v2_run_id: this.v2RunId,
            metadata: {
              step_name: eventData.data?.step_name,
              tokens_used: eventData.data?.tokens_used,
              cost: eventData.data?.cost
            }
          }
        });
        break;

      case 'connection.established':
        console.log('✅ FRONTEND DEBUG: V2 Connection established:', eventData.data || eventData);
        break;

      default:
        console.log('⚠️ FRONTEND DEBUG: Unhandled V2 event type:', eventType);
    }
  }

  // NEW: Get session ID from run ID for V2 workflows
  async getSessionIdFromRunId(runId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v2/workflows/${runId}/execution-details`, {
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const sessionId = data.chat_session_id;
        console.log('🔍 FRONTEND DEBUG: Got session_id for run_id:', { runId, sessionId });
        return sessionId;
      } else {
        console.error('❌ FRONTEND DEBUG: Failed to get session_id:', response.status);
        return null;
      }
    } catch (error) {
      console.error('❌ FRONTEND DEBUG: Error getting session_id:', error);
      return null;
    }
  }

  // NEW: Poll for new messages with timeout (for workflow continuation)
  async pollForNewMessages(runId, startingMessageCount, maxAttempts = 10) {
    console.log('🔍 FRONTEND DEBUG: Starting to poll for new messages...');
    console.log(`🔍 POLLING CONFIG: runId=${runId}, startCount=${startingMessageCount}, maxAttempts=${maxAttempts}`);

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        // Get session ID
        const sessionId = await this.getSessionIdFromRunId(runId);
        if (!sessionId) {
          console.error('❌ FRONTEND DEBUG: Could not get session_id for polling');
          return;
        }

        // Check current message count
        const messages = await this.loadSession(sessionId);
        const currentCount = messages.length;

        console.log(`🔍 FRONTEND DEBUG: Poll attempt ${attempt}/${maxAttempts} - Messages: ${currentCount} (was ${startingMessageCount})`);

        if (currentCount > startingMessageCount) {
          // New messages found! Fetch and emit them
          console.log('🎉 FRONTEND DEBUG: New messages detected, fetching...');
          await this.fetchAndEmitV2Messages(runId, startingMessageCount);
          return;
        }

        // Wait before next attempt - use shorter delay for faster response
        // For long-running operations after modal idle, we need more attempts
        const delay = 2000; // Fixed 2 second delay between attempts
        if (attempt < maxAttempts) {
          console.log(`⏱️ FRONTEND DEBUG: Waiting ${delay}ms before next poll (attempt ${attempt}/${maxAttempts})...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      } catch (error) {
        console.error(`❌ FRONTEND DEBUG: Error during poll attempt ${attempt}:`, error);
      }
    }

    console.log('⏰ FRONTEND DEBUG: Polling timeout - no new messages detected after', maxAttempts, 'attempts');
    console.log('⚠️ FRONTEND DEBUG: Please refresh the page to see the latest messages');
  }

  // NEW: Get run_id from session_id for continuing existing V2 workflows
  async getRunIdFromSessionId(sessionId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v2/workflows/session/${sessionId}/run-id`, {
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const runId = data.run_id;
          console.log('🔍 FRONTEND DEBUG: Got run_id for session_id:', { sessionId, runId });
          return runId;
        } else {
          console.log('ℹ️ FRONTEND DEBUG: No run_id found for session:', sessionId);
          return null;
        }
      } else {
        console.error('❌ FRONTEND DEBUG: Failed to get run_id:', response.status);
        return null;
      }
    } catch (error) {
      console.error('❌ FRONTEND DEBUG: Error getting run_id:', error);
      return null;
    }
  }

  // Close WebSocket connection
  disconnect() {
    if (this.wsConnection) {
      this.wsConnection.close();
      this.wsConnection = null;
    }
    this.emitStatusChange('disconnected');
  }

  // Cleanup resources
  destroy() {
    this.disconnect();
    this.messageHandlers.clear();
    this.statusHandlers.clear();
  }
}

export default GenericChatService;